import { serverURL } from './config'
import type { WizardData } from '../types'

export interface OnboardingProgress {
  id: number
  org_uuid: string
  current_step: number
  max_step_reached: number
  funding_need_id: number | null
  scan_job_id: string | null
  application_id: string | null
  scan_result_id: string | null
  profile_quality_passed: boolean | null
  funding_quality_passed: boolean | null
  stale_from: number | null
  profile_generated: boolean
  wizard_data: WizardData | null
  is_complete: boolean
  completed_at: string | null
  created_at: string
  updated_at: string
  last_updated_by: string | null
}

export interface SaveProgressPayload {
  current_step: number
  max_step_reached: number
  funding_need_id?: number | null
  scan_job_id?: string | null
  application_id?: string | null
  scan_result_id?: string | null
  funding_quality_passed?: boolean | null
  stale_from?: number | null
  profile_generated?: boolean
  wizard_data?: WizardData | null
}

export async function loadProgress(
  token: string,
  orgUuid: string
): Promise<OnboardingProgress | null> {
  const response = await fetch(
    `${serverURL}/onboarding-progress/${orgUuid}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (response.status === 404) return null

  if (!response.ok) {
    throw new Error(
      `Error loading onboarding progress: ${response.statusText}`
    )
  }

  return response.json()
}

export async function saveProgress(
  token: string,
  orgUuid: string,
  data: SaveProgressPayload
): Promise<OnboardingProgress> {
  const response = await fetch(
    `${serverURL}/onboarding-progress/${orgUuid}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  )

  if (!response.ok) {
    throw new Error(
      `Error saving onboarding progress: ${response.statusText}`
    )
  }

  return response.json()
}

export async function completeProgress(
  token: string,
  orgUuid: string
): Promise<void> {
  const response = await fetch(
    `${serverURL}/onboarding-progress/${orgUuid}/complete`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    // Prefer the server's own detail (e.g. "Onboarding has not reached
    // match results yet") over statusText -- a bare "Bad Request" makes
    // this indistinguishable from any other 400 in logs/Sentry.
    let detail = response.statusText
    try {
      const body = await response.json()
      if (typeof body?.detail === 'string') {
        detail = body.detail
      }
    } catch {
      // Not JSON (or empty body) -- fall back to statusText.
    }
    throw new Error(`Error completing onboarding progress: ${detail}`)
  }
}
